import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { FaGift, FaShoppingBag, FaArrowLeft, FaHandshake, FaCogs, FaLaptop, FaDollarSign, FaBullhorn, FaUsers } from 'react-icons/fa';
import { collection, query, getDocs, addDoc, serverTimestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';

const PortalConference = () => {
  const { darkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [hasChosen, setHasChosen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null); // 'giveaway' or 'giveaway+resource'
  const [activeTab, setActiveTab] = useState('Favorites');
  const [activeSection, setActiveSection] = useState(null);
  const calendlyWidgetRef = useRef(null);
  
  // Form state for giveaway
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    businessName: '',
    additionalInfo: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  // Find starred conference
  const findStarredConference = async () => {
    try {
      const conferencesRef = collection(db, 'conferences');
      const q = query(conferencesRef, where('starred', '==', true));
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        // Return the first starred conference
        return snapshot.docs[0].id;
      }
      return null;
    } catch (error) {
      console.error('Error finding starred conference:', error);
      return null;
    }
  };

  // Save lead to starred conference
  const saveLeadToConference = async (conferenceId, leadData) => {
    try {
      const leadsRef = collection(db, 'conferences', conferenceId, 'leads');
      await addDoc(leadsRef, {
        ...leadData,
        date: serverTimestamp(),
        createdAt: serverTimestamp()
      });
      console.log('Lead saved to conference:', conferenceId);
    } catch (error) {
      console.error('Error saving lead to conference:', error);
      throw error;
    }
  };

  // Check for booking success parameter on mount
  useEffect(() => {
    const bookingSuccess = searchParams.get('booking');
    if (bookingSuccess === 'success') {
      // Save lead data for Calendly booking (giveaway+resource)
      // Note: Calendly data would come from webhooks, but we can save a basic entry here
      const saveCalendlyBooking = async () => {
        try {
          const starredConferenceId = await findStarredConference();
          if (starredConferenceId) {
            // Get data from URL params if Calendly passes them, otherwise use minimal data
            const email = searchParams.get('email') || '';
            const name = searchParams.get('name') || '';
            const [firstName = '', lastName = ''] = name.split(' ');
            
            if (email) {
              await saveLeadToConference(starredConferenceId, {
                firstName: firstName || 'Unknown',
                lastName: lastName || 'Unknown',
                email: email,
                phone: searchParams.get('phone') || '',
                companyName: searchParams.get('company') || '',
                booked: true,
                source: 'Conference Portal - Calendly',
                entryType: 'Giveaway + Resource',
                calendlyBooking: true
              });
            }
          }
        } catch (error) {
          console.error('Error saving Calendly booking:', error);
        }
      };
      
      saveCalendlyBooking();
      
      // Automatically show the content view (tabs and sidebar)
      setHasChosen(true);
      setSelectedOption(null); // Set to null to show tabs/content view
      // Remove the query parameter from URL
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Preload Calendly script on component mount
  useEffect(() => {
    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
    
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []); // Run once on mount

  // Initialize Calendly widget when giveaway+resource option is selected
  useEffect(() => {
    if (selectedOption === 'giveaway+resource' && calendlyWidgetRef.current) {
      const initializeWidget = () => {
        if (!calendlyWidgetRef.current) return;

        const widgetElement = calendlyWidgetRef.current;
        // Check if widget is already initialized (has iframe)
        const hasIframe = widgetElement.querySelector('iframe');
        
        if (!hasIframe) {
          if (window.Calendly && window.Calendly.initInlineWidget) {
            try {
              window.Calendly.initInlineWidget({
                url: `https://calendly.com/d/ctjt-ptt-7cb/bba-discovery-call?hide_gdpr_banner=1&primary_color=ff0400&redirect_url=${encodeURIComponent(window.location.origin + '/portal/conference?booking=success')}`,
                parentElement: widgetElement
              });
            } catch (error) {
              console.error('Error initializing Calendly widget:', error);
              // Retry after a delay
              setTimeout(initializeWidget, 500);
            }
          } else {
            // Calendly not ready yet, wait and retry
            setTimeout(() => {
              if (window.Calendly && window.Calendly.initInlineWidget) {
                initializeWidget();
              } else {
                // Force re-initialization by temporarily removing and re-adding the class
                widgetElement.classList.remove('calendly-inline-widget');
                setTimeout(() => {
                  widgetElement.classList.add('calendly-inline-widget');
                }, 50);
              }
            }, 200);
          }
        }
      };

      // Wait a bit for the DOM to update, then initialize
      const timeoutId = setTimeout(initializeWidget, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedOption]);

  const tabs = ['Favorites', 'Sales', 'Operations', 'Finance', 'Marketing', 'Technology', 'HR'];
  
  // Example sections - can be customized based on activeTab
  const sections = [
    'All Items',
    'Software',
    'Tools',
    'Services',
    'Resources',
    'Templates',
    'Training'
  ];

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormErrors({});

    // Basic validation
    const errors = {};
    if (!formData.firstName.trim()) errors.firstName = 'First name is required';
    if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.phone.trim()) errors.phone = 'Phone is required';
    if (!formData.businessName.trim()) errors.businessName = 'Business name is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setIsSubmitting(false);
      return;
    }

    try {
      // Find starred conference
      const starredConferenceId = await findStarredConference();
      
      if (!starredConferenceId) {
        console.warn('No starred conference found. Lead not saved to conference.');
        // Still proceed with form submission flow
      } else {
        // Determine if booked based on selected option
        const booked = selectedOption === 'giveaway+resource';
        
        // Prepare lead data
        const leadData = {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          companyName: formData.businessName.trim(),
          booked: booked,
          additionalInfo: formData.additionalInfo.trim() || null,
          source: 'Conference Portal',
          entryType: selectedOption === 'giveaway' ? 'Giveaway Only' : 'Giveaway + Resource'
        };

        // Save to starred conference
        await saveLeadToConference(starredConferenceId, leadData);
      }
      
      if (selectedOption === 'giveaway') {
        // Show thank you page for giveaway only
        setShowThankYou(true);
      }
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        businessName: '',
        additionalInfo: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error submitting your entry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden">
      <PortalBackground />
      <div className="relative w-full px-4 md:px-6 lg:px-8 mx-auto max-w-7xl">
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        @keyframes float-subtle {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        .blueprint-float {
          animation: float 3s ease-in-out infinite;
        }
        .blueprint-float-subtle {
          animation: float-subtle 4s ease-in-out infinite;
        }
      `}</style>
      {/* Header */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-4">
          <img 
            src="/bba-emblem.svg" 
            alt="BBA Emblem" 
            className="h-24 w-auto"
          />
        </div>
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
          Builders Breakthrough Advisors
        </h1>
      </div>

      {/* Initial Two Cards View */}
      {!hasChosen ? (
        <>
          <div className="flex flex-col md:flex-row justify-center gap-4 md:gap-6 items-stretch">
            {/* Left Card - Enter Giveaway */}
            <button
              onClick={() => {
                setSelectedOption('giveaway');
                setHasChosen(true);
              }}
              className="w-full md:flex-1 md:max-w-md h-full min-h-[280px] min-w-0 bg-gradient-to-br from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 rounded-lg shadow-lg p-4 md:p-6 lg:p-10 text-white transition-all duration-200 md:transform md:hover:scale-105 flex flex-col justify-center overflow-hidden"
            >
              <div className="flex flex-col items-center text-center w-full max-w-full">
                <div className="mb-4">
                  <img src="/ipad.svg" alt="iPad" className="h-16 sm:h-20 md:h-24 w-auto blueprint-float-subtle" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2 px-2">Enter Raffle</h2>
                <p className="text-base sm:text-lg opacity-90 px-2">Join the raffle to win an iPad</p>
              </div>
            </button>

            {/* Right Card - Enter Giveaway + Free Resource */}
            <button
              onClick={() => {
                setSelectedOption('giveaway+resource');
                setHasChosen(true);
              }}
              className="w-full md:flex-1 md:max-w-md h-full min-h-[280px] min-w-0 bg-gradient-to-br from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 rounded-lg border-4 border-red-500 shadow-lg p-4 md:p-6 lg:p-10 text-white transition-all duration-200 md:transform md:hover:scale-105 flex flex-col justify-center overflow-hidden"
            >
              <div className="flex flex-col items-center text-center w-full max-w-full">
                <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4 w-full max-w-full px-2">
                  <img src="/ipad.svg" alt="iPad" className="h-16 sm:h-20 md:h-24 w-auto flex-shrink-0 blueprint-float-subtle" />
                  <img src="/blue.svg" alt="Blueprint" className="h-16 sm:h-20 md:h-24 w-auto flex-shrink-0 blueprint-float-subtle max-w-[80px] sm:max-w-[96px] md:max-w-none" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2 px-2">Enter Raffle + <span className="text-red-600 dark:text-red-500">$20M Blueprint</span></h2>
                <p className="text-base sm:text-lg opacity-90 px-2">Join the raffle and get a free copy</p>
              </div>
            </button>
          </div>

          {/* $20M Blueprint Highlight Section */}
          <div className="max-w-6xl mx-auto mb-8 mt-0 px-4 md:px-0">
          <div className="p-4 md:p-8">
            <div className="text-center mb-6">
              <h3 className="text-xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">
                Scale with our proven systems using <span className="text-red-600 dark:text-red-500">The $20M Blueprint</span>
              </h3>
            </div>

            {/* Pillars Grid and Blueprint Image */}
            <div className="flex flex-col lg:flex-row gap-6 mt-8">
              {/* Cards Grid - 2/3 width */}
              <div className="w-full lg:w-2/3">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Sales */}
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-red-200 dark:border-red-800 shadow-sm text-center">
                    <FaHandshake className="text-4xl text-red-500 mx-auto mb-3" />
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-3">Realtor Partnerships</h4>
                    <span className="inline-block px-3 py-1 text-sm font-semibold text-white bg-red-500 rounded-full">Sales</span>
                  </div>
                  {/* Operations */}
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-red-200 dark:border-red-800 shadow-sm text-center">
                    <FaCogs className="text-4xl text-blue-500 mx-auto mb-3" />
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-3">Hiring Roadmap</h4>
                    <span className="inline-block px-3 py-1 text-sm font-semibold text-white bg-blue-500 rounded-full">Operations</span>
                  </div>
                  {/* Technology */}
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-red-200 dark:border-red-800 shadow-sm text-center">
                    <FaLaptop className="text-4xl text-purple-500 mx-auto mb-3" />
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-3">Tech Stack</h4>
                    <span className="inline-block px-3 py-1 text-sm font-semibold text-white bg-purple-500 rounded-full">Technology</span>
                  </div>
                  {/* Finance */}
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-red-200 dark:border-red-800 shadow-sm text-center">
                    <FaDollarSign className="text-4xl text-green-500 mx-auto mb-3" />
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-3">Pricing Metrics</h4>
                    <span className="inline-block px-3 py-1 text-sm font-semibold text-white bg-green-500 rounded-full">Finance</span>
                  </div>
                  {/* Marketing */}
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-red-200 dark:border-red-800 shadow-sm text-center">
                    <FaBullhorn className="text-4xl text-orange-500 mx-auto mb-3" />
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-3">Lead Machine</h4>
                    <span className="inline-block px-3 py-1 text-sm font-semibold text-white bg-orange-500 rounded-full">Marketing</span>
                  </div>
                  {/* HR */}
                  <div className="bg-white dark:bg-gray-900 rounded-xl p-5 border border-red-200 dark:border-red-800 shadow-sm text-center">
                    <FaUsers className="text-4xl text-yellow-500 mx-auto mb-3" />
                    <h4 className="font-bold text-gray-900 dark:text-white text-lg mb-3">Onboarding Systems</h4>
                    <span className="inline-block px-3 py-1 text-sm font-semibold text-white bg-yellow-500 rounded-full">HR</span>
                  </div>
                </div>
              </div>
              
              {/* Blueprint Image - 1/3 width */}
              <div className="w-full lg:w-1/3 flex items-center justify-center">
                <img src="/blue.svg" alt="$20M Blueprint" className="w-full h-auto max-w-xs blueprint-float" />
              </div>
            </div>
          </div>
        </div>
        </>
      ) : selectedOption === 'giveaway' && showThankYou ? (
        /* Thank You Page */
        <div className="max-w-5xl mx-auto">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-8">
            <div className="mb-6 text-center">
              <FaGift className="text-6xl text-red-500 mx-auto mb-4" />
              <h2 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
                Thank You!
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Your giveaway entry has been submitted successfully.
              </p>
            </div>

            {/* Claim Free Resource Section with Blueprint Image */}
            <div className="flex flex-col md:flex-row gap-6 items-center">
              {/* Content Section - 1/2 width */}
              <div className="w-full md:w-1/2 flex items-center justify-center">
                <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-6 text-center w-full">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                    Want to Implement Proven Systems to Scale?
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">
                    Don't forget to claim your free copy of The $20M Blueprint!
                  </p>
                  <div className="flex justify-center gap-4">
                    <button
                      onClick={() => {
                        setSelectedOption('giveaway+resource');
                        setShowThankYou(false);
                      }}
                      className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors duration-200"
                    >
                      Claim Free Resource
                    </button>
                    <button
                      onClick={() => {
                        setHasChosen(false);
                        setSelectedOption(null);
                        setShowThankYou(false);
                      }}
                      className="px-6 py-3 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-lg transition-colors duration-200"
                    >
                      No Thanks
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Blueprint Image - 1/2 width */}
              <div className="w-full md:w-1/2 flex items-center justify-center">
                <img src="/blue.svg" alt="$20M Blueprint" className="w-full h-auto max-w-xs blueprint-float" />
              </div>
            </div>
          </div>
        </div>
      ) : selectedOption === 'giveaway' ? (
        /* Giveaway Form */
        <div className="max-w-2xl mx-auto">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-8">
            <div className="flex items-center justify-center relative mb-6">
              <button
                onClick={() => {
                  setHasChosen(false);
                  setSelectedOption(null);
                }}
                className="absolute left-0 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
              >
                <FaArrowLeft className="text-xl" />
              </button>
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                Enter Raffle
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* First Name */}
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                  />
                  {formErrors.firstName && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.firstName}</p>
                  )}
                </div>

                {/* Last Name */}
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                  />
                  {formErrors.lastName && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.lastName}</p>
                  )}
                </div>
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                />
                {formErrors.email && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                />
                {formErrors.phone && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.phone}</p>
                )}
              </div>

              {/* Business Name */}
              <div>
                <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="businessName"
                  required
                  value={formData.businessName}
                  onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                />
                {formErrors.businessName && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.businessName}</p>
                )}
              </div>

              {/* Additional Info (Optional) */}
              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Anything else you'd like to add?
                </label>
                <textarea
                  id="additionalInfo"
                  rows={4}
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                  placeholder="Optional..."
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Entry'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : selectedOption === 'giveaway+resource' ? (
        /* Giveaway + Resource - Just Calendly */
        <div className="max-w-4xl mx-auto">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-8">
            <div className="flex items-center justify-center relative mb-6">
              <button
                onClick={() => {
                  setHasChosen(false);
                  setSelectedOption(null);
                }}
                className="absolute left-0 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200"
              >
                <FaArrowLeft className="text-xl" />
              </button>
              <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
                Enter Raffle + <span className="text-red-600 dark:text-red-500">$20M Blueprint</span>
              </h2>
            </div>
            <div className="text-center mb-6">
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">
                Fill out your information and book a discovery call to receive your $20M blueprint on Monday!
              </p>
            </div>
            
            {/* Calendly Widget */}
            <div 
              ref={calendlyWidgetRef}
              className="calendly-inline-widget" 
              style={{ minWidth: '320px', height: '700px' }}>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Tabs - Centered */}
          <div className="mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex justify-center space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  py-4 px-1 border-b-2 font-medium text-sm transition-colors duration-200
                  ${
                    activeTab === tab
                      ? 'border-red-500 text-red-500 dark:text-red-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content Area with Sidebar */}
      <div className="flex gap-6">
        {/* Left Sidebar - Vertical Document Tabs */}
        <div className="w-64 flex-shrink-0">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Sections
              </h2>
            </div>
            <div className="py-2">
              {sections.map((section, index) => (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`
                    w-full text-left px-4 py-3 text-sm font-medium transition-colors duration-200
                    border-l-4
                    ${
                      activeSection === section
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-500 text-red-600 dark:text-red-400'
                        : 'border-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  {section}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1">
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-6 min-h-[400px]">
            {/* Content will go here based on activeTab and activeSection */}
            <div className="text-center text-gray-500 dark:text-gray-400 py-12">
              <p>Select a section to view items</p>
              {activeTab && (
                <p className="text-sm mt-2">Active Tab: {activeTab}</p>
              )}
              {activeSection && (
                <p className="text-sm mt-1">Active Section: {activeSection}</p>
              )}
            </div>
          </div>
        </div>
      </div>
        </>
      )}
      </div>
    </div>
  );
};

export default PortalConference;


